import { useWallet } from "@manahippo/aptos-wallet-adapter";
import { Slider } from "@mui/material";
import { useEffect, useMemo, useState } from "react";
import { useDispatch } from "react-redux";

import { foil_swap_address } from "../../libs/address";
import {
  getTransactionByHash,
  getStakedLpBalance,
  getGaugeIdFromPool,
} from "../../libs/aptos_sdk";
import {
  gauge_withdraw,
  get_coin_icon,
  get_coin_short_name,
  getDecimalsAmount,
  getIntAmountFromDecimals,
} from "../../libs/swap_sdk";
import { useAppSelector } from "../../store/hooks";
import { end_loading, start_loading } from "../../store/loading";
import { sleep } from "../../utils/common";
import Button from "../Common/Button";
import TransactionDialog from "../Common/TransactionDialog";
import Divider from "../Divider";

type Props = {
  coinX: string;
  coinY: string;
};

export default function UnstakeTab(props: Props) {
  const dispatch = useDispatch();
  const { signAndSubmitTransaction } = useWallet();
  const { coinX, coinY } = props;
  const accountAddresses = useAppSelector(
    (state: any) => state.wallet.accountAddress
  );

  const coinInfoIconX = get_coin_icon(coinX);
  const coinInfoIconY = get_coin_icon(coinY);
  const [unstakeText, setUnstakeText] = useState("Enter an amount");
  const [unstakeRate, setUnstakeRate] = useState<number>(0);
  const [openAlert, setOpenAlert] = useState<boolean>(false);
  const [step, setStep] = useState(0);
  const [txn, setTxn] = useState("");

  const [amountUnstake, setAmountUnstake] = useState<number>(0);
  const [amountMaxUnstake, setAmountMaxUnstake] = useState<number>(0);

  useMemo(() => {
    dispatch(start_loading());
    const get = async () => {
      const amountXY = await getStakedLpBalance(accountAddresses, coinX, coinY);
      if (Number(amountXY) > 0) {
        // @ts-ignore
        setAmountMaxUnstake(Number(amountXY));
      }
      dispatch(end_loading());
    };
    get();
  }, [dispatch, accountAddresses, coinX, coinY]);

  const handleCloseAlert = () => setOpenAlert(false);
  const handleUnstake = async () => {
    setOpenAlert(true);
    setTxn("");
    setStep(0);
    const pool = `${foil_swap_address}::swap::LPToken<${coinX}, ${coinY}>`;

    const gauge_id = await getGaugeIdFromPool(pool);
    const data = await gauge_withdraw(
      signAndSubmitTransaction,
      pool,
      gauge_id,
      getIntAmountFromDecimals(amountUnstake, 9)
    );
    window.console.info("tx", data);
    setTxn(data.hash);

    setStep(1);
    let transaction;
    while (!transaction) {
      try {
        transaction = await getTransactionByHash(data.hash);
        window.console.info("transaction", transaction);
      } catch (e) {
        await sleep(500);
        window.console.error(e);
      }
    }
    setStep(2);
  };

  useEffect(() => {
    if (amountUnstake <= 0) {
      setUnstakeText("Enter an amount");
    } else if (amountUnstake > amountMaxUnstake) {
      setUnstakeText(`Exceed staked amount`);
    } else {
      setUnstakeText("Unstake");
    }
  }, [amountUnstake, amountMaxUnstake]);

  const handleSliderChange = (event: Event, newValue: number | number[]) => {
    if (Array.isArray(newValue)) {
      return;
    }
    setUnstakeRate(newValue);
    // const share = newValue * Math.pow(10, lp_decimals)/supplyXY;
    setAmountUnstake(newValue);
  };

  return (
    <>
      <div className="p-4">
        <div className="flex flex-col gap-2">
          <div className="flex flex-col gap-3">
            <div className="text-white text-opacity-50">
              {" "}
              Select the percentage of your position to unstake:
            </div>
            <div className="flex items-center gap-8">
              <div className="flex w-1/4">
                <p className="text-white text-opacity-50 bg-white bg-opacity-20 rounded-lg p-2">
                  {(
                    (Number(unstakeRate) * 100) /
                    (amountMaxUnstake / Math.pow(10, 9))
                  ).toFixed(1)}
                  % = {unstakeRate}
                </p>
              </div>
              <div className="w-full">
                <Slider
                  step={0.00000001}
                  onChange={handleSliderChange}
                  max={Number(amountMaxUnstake) / Math.pow(10, 9)}
                  value={unstakeRate}
                  defaultValue={0}
                  aria-label="Default"
                  valueLabelDisplay="auto"
                />
              </div>
            </div>
          </div>
          <Divider padding={3} />

          <div className="flex justify-between items-start bg-primary-100 rounded-lg p-2">
            <div className="flex flex-col justify-end items-end">
              <p className="text-white text-4xl">{unstakeRate}</p>
              {/* <p className="text-white">$: --</p> */}
            </div>
            <div className="flex flex-col items-center gap-2">
              <div className="flex items-center gap-2 bg-primary-200 rounded-full p-2">
                <img alt="Coin" className="w-6 h-6" src={coinInfoIconX.icon} />
                <img alt="Coin" className="w-6 h-6" src={coinInfoIconY.icon} />
                <span className="text-white text-[15px] font-semibold">
                  {get_coin_short_name(coinX)} - {get_coin_short_name(coinY)} LP
                </span>
              </div>
              <div className="text-white text-opacity-50">
                Max Withdrawal: {getDecimalsAmount(amountMaxUnstake, 9)}
              </div>
            </div>
          </div>
        </div>
      </div>
      <div className="p-4">
        <Button
          fullWidth
          onClick={handleUnstake}
          disabled={unstakeText !== "Unstake"}
          variant="contained"
        >
          {unstakeText}
        </Button>
      </div>
      <div>
        <TransactionDialog
          openAlert={openAlert}
          txn={txn}
          title=""
          step={step}
          handleCloseAlert={handleCloseAlert}
        />
      </div>
    </>
  );
}
